// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Pressable, StyleSheet, type StyleProp, type ViewStyle } from 'react-native';

import { Text, View } from '@/components/Themed';
import { t } from '@/i18n';
import appColors, { alpha } from '@/theme/colors';
import { panelTextShadow } from '@/theme/textShadow';

export const WEIGHT_STEPS = [25, 50, 75, 100, 150] as const;

const QUADRANT_INDEXES = [0, 1, 2, 3] as const;

type GameWeightControlProps = {
  weightPercent: number;
  /** Omit to render the weight as a read-only chip — the setting itself lives in the game setup modal. */
  onPress?: () => void;
  selected?: boolean;
  centered?: boolean;
  showLabel?: boolean;
  size?: 'default' | 'compact';
  accessibilityLabel?: string;
  /** Extra style for the row, e.g. to left-align it against a block of text. */
  style?: StyleProp<ViewStyle>;
  /** Optional testID for the weight chip, so E2E can read or cycle a specific game's merit weight. */
  testID?: string;
};

export default function GameWeightControl({
  weightPercent,
  onPress,
  selected = false,
  centered = false,
  showLabel = true,
  size = 'default',
  accessibilityLabel,
  style,
  testID,
}: GameWeightControlProps) {
  const normalizedPercent = WEIGHT_STEPS.includes(weightPercent as (typeof WEIGHT_STEPS)[number]) ? weightPercent : 100;
  const activeCount = Math.max(0, Math.min(4, Math.round(normalizedPercent / 25)));
  const boosted = normalizedPercent > 100;
  const compact = size === 'compact';
  const chipStyle = [
    styles.weightCircle,
    compact && styles.weightCircleCompact,
    selected && styles.weightCircleSelected,
  ];
  const label = accessibilityLabel ?? `${t('tournaments.gameWeightLabel')} ${normalizedPercent}%`;
  const chipContent = (
    <>
      <View style={[styles.weightQuad, compact && styles.weightQuadCompact]}>
        {QUADRANT_INDEXES.map((index) => {
          const quadrantStyle =
            index === 0
              ? styles.weightQuadTopLeft
              : index === 1
                ? styles.weightQuadTopRight
                : index === 2
                  ? styles.weightQuadBottomRight
                  : styles.weightQuadBottomLeft;
          const isActive = index < activeCount;
          return (
            <View
              key={index}
              style={[
                styles.weightQuadCell,
                compact && styles.weightQuadCellCompact,
                quadrantStyle,
                isActive ? (boosted ? styles.weightQuadBoosted : styles.weightQuadActive) : styles.weightQuadInactive,
              ]}
            />
          );
        })}
      </View>
      <Text style={[styles.weightCircleText, compact && styles.weightCircleTextCompact]}>{normalizedPercent}%</Text>
    </>
  );

  return (
    <View style={[styles.weightRow, centered && styles.weightRowCentered, compact && styles.weightRowCompact, style]}>
      {showLabel ? <Text style={styles.weightLabel}>{t('tournaments.gameWeightLabel')}</Text> : null}
      {onPress ? (
        <Pressable
          testID={testID}
          style={chipStyle}
          onPress={onPress}
          accessibilityRole="button"
          accessibilityLabel={label}
        >
          {chipContent}
        </Pressable>
      ) : (
        // No handler: a plain View rather than a disabled button, so a screen reader reads the
        // weight as the value it is instead of announcing a control that cannot be operated.
        <View testID={testID} style={chipStyle} accessibilityLabel={label}>
          {chipContent}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  weightRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 6,
  },
  weightRowCentered: {
    justifyContent: 'center',
  },
  weightRowCompact: {
    marginTop: 0,
  },
  weightLabel: {
    color: appColors.text.primary,
    fontSize: 11,
    fontWeight: '700',
  },
  weightCircle: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: alpha(appColors.accent.glowBright, 0.12),
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  weightCircleCompact: {
    width: 34,
    height: 34,
    borderRadius: 17,
  },
  weightCircleSelected: {
    backgroundColor: alpha(appColors.accent.glowBright, 0.16),
  },
  weightQuad: {
    width: 34,
    height: 34,
    borderRadius: 17,
    overflow: 'hidden',
    position: 'absolute',
  },
  weightQuadCompact: {
    width: 26,
    height: 26,
    borderRadius: 13,
  },
  weightQuadCell: {
    position: 'absolute',
    width: 17,
    height: 17,
  },
  weightQuadCellCompact: {
    width: 13,
    height: 13,
  },
  weightQuadActive: {
    backgroundColor: appColors.status.warningBorder,
  },
  weightQuadBoosted: {
    backgroundColor: appColors.accent.orange,
  },
  weightQuadInactive: {
    backgroundColor: alpha(appColors.accent.glow, 0.24),
  },
  weightQuadTopLeft: {
    top: 0,
    left: 0,
  },
  weightQuadTopRight: {
    top: 0,
    right: 0,
  },
  weightQuadBottomLeft: {
    bottom: 0,
    left: 0,
  },
  weightQuadBottomRight: {
    bottom: 0,
    right: 0,
  },
  weightCircleText: {
    color: appColors.text.primary,
    fontSize: 10,
    fontWeight: '700',
    ...panelTextShadow(0.55, 1, 1),
  },
  weightCircleTextCompact: {
    fontSize: 9,
  },
});
